<template>
  <div>
    <div
      v-if="source === 'nosto'"
      :id="nostoPlacementId"
      class="nosto_element"
    />
    <div
      v-else
      class="cart-cross-sell"
    >
      <div class="cart-cross-sell__info-wrapper">
        <div class="cart-cross-sell__info">
          <div
            class="cart-cross-sell__info-inner"
          >
            <h2
              v-if="title && products.length > 0"
              class="cart-cross-sell__title h4"
              v-text="title"
            />
          </div>
        </div>

        <div class="cart-cross-sell__navigation">
          <div
            ref="progressBar"
            class="cart-cross-sell__progress"
          />

          <button
            ref="prevButton"
            class="
            cart-cross-sell__button
            cart-cross-sell__button-prev
            button
            button--nav-arrow
            button--text
          "
            type="button"
            :aria-label="$string('accessibility.carousel.previous')"
            modifiers="text"
          >
            <span
              class="button__label visually-hidden"
              v-text="$string('accessibility.carousel.previous')"
            />

            <span
              class="button__icon"
            >
              <icon-chevron-left />
            </span>
          </button>

          <button
            ref="nextButton"
            class="
            cart-cross-sell__button
            button
            button--nav-arrow
            button--text
          "
            type="button"
            :aria-label="$string('accessibility.carousel.next')"
            modifiers="text"
          >
            <span
              class="button__label visually-hidden"
              v-text="$string('accessibility.carousel.next')"
            />

            <span
              class="button__icon"
            >
              <icon-chevron-right />
            </span>
          </button>
        </div>
      </div>

      <template v-if="ready">
        <swiper
          class="cart-cross-sell__content"
          :slides-per-view="swiperOptions.slidesPerView"
          :space-between="swiperOptions.spaceBetween"
          :loop="swiperOptions.loop"
          :breakpoints="swiperOptions.breakpoints"
          a11y
          :modules="modules"
          :navigation="{
            nextEl: $refs.nextButton,
            prevEl: $refs.prevButton,
          }"
          :pagination="{
            el: $refs.progressBar,
            type: 'progressbar',
          }"
          @swiper="onSwiper"
        >
          <swiper-slide
            v-for="(product, index) of products"
            :key="`cart-cross-sell-${product.id}`"
          >
            <product-card
              :index="index"
              :product="product"
              class="cart-cross-sell__product-card"
              @product-card-quick-add-open="disableTouch"
              @product-card-quick-add-close="enableTouch"
            />
          </swiper-slide>
        </swiper>
      </template>

      <template v-else>
        <div class="cart-cross-sell__content" />
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Cart Cross-Sell (cart-cross-sell)
 * -----------------------------------------------------------------------------
 * Carousel with product cards.
 *
 * @param {String} source - 'collection' or 'nosto' from settings.
 * @param {Object} collectionHandle - Liquid collection handle.
 * @param {Number} productLimit - Product limit set in section settings.
 * @param {Object} title - Title.
 * @param {String} nostoPlacementId - Nosto placement identifier.
 *
 */

import collection from '~/graphql/queries/collection.gql'
import { formatCollection } from '~/helpers/format'

import { A11y, Navigation, Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'

import ProductCard from '~async/product-card/product-card'
import IconChevronLeft from '~icons/direction/chevron/left-narrow.svg'
import IconChevronRight from '~icons/direction/chevron/right-narrow.svg'

export default {
  name: 'CartCrossSell',

  components: {
    IconChevronLeft,
    IconChevronRight,
    ProductCard,
    Swiper,
    SwiperSlide,
  },

  props: {
    source: {
      type: String,
      default: 'collection',
    },
    collectionHandle: {
      type: String,
      default: '',
    },
    productLimit: {
      type: Number,
      default: 9,
    },
    title: {
      type: String,
      default: '',
    },
    nostoPlacementId: {
      type: String,
      default: '',
    },
  },

  setup() {
    return {
      modules: [
        A11y,
        Navigation,
        Pagination,
      ],
    }
  },

  data() {
    return {
      ready: false,
      products: [],
      swiper: null,
      swiperOptions: {
        autoHeight: true,
        slidesPerView: 2.1,
        spaceBetween: 8,
        loop: true,
        breakpoints: {
          1024: {
            slidesPerView: 2.15,
          },
        },
      },
    }
  },

  mounted() {
    if (!this.source === 'collection') {
      return
    }

    this.getCollectionProducts(this.collectionHandle)
  },

  methods: {

    /**
     * Set reference for Swiper carousel.
     */
    onSwiper(swiper) {
      this.swiper = swiper
    },

    /**
     * Disable swiper touch.
     */
    disableTouch() {
      this.swiper.allowTouchMove = false
    },

    /**
     * Enable swiper touch.
     */
    enableTouch() {
      this.swiper.allowTouchMove = true
    },

    /**
     * Query collection and format response.
     * @param {String} handle - Collection handle to load.
     * @param {Number} first - How many products to load.
     * @returns {Array}
     */
    async getCollectionProducts(handle, first = this.productLimit) {
      try {
        const response = await cnvs.Query({
          query: collection,
          variables: {
            country: cnvs.store.country,
            first,
            handle,
            language: cnvs.store.language,
          },
        })

        const collectionFormatted = formatCollection(response)

        this.products = collectionFormatted.products
        this.ready = true

      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
  },
}
</script>

<style lang="scss">
  @import './cart-cross-sell';
</style>
